import {
  IEmailAttachment,
  IMedia,
} from '@principle-theorem/principle-core/interfaces';
import {
  asyncForEach,
  AtLeast,
  DocumentReference,
  Firestore,
  toMoment,
} from '@principle-theorem/shared';
import { compact } from 'lodash';
import { getDownloadURL, ref, type FirebaseStorage } from 'firebase/storage';

export class Media {
  static init(overrides: AtLeast<IMedia, 'path'>): IMedia {
    return {
      name: '',
      tags: [],
      deleted: false,
      ...overrides,
    };
  }

  static hasExpired(media: IMedia): boolean {
    if (!media.expiresAt) {
      return false;
    }
    return toMoment(media.expiresAt).isBefore();
  }

  /**
   * Downloads a media file from Firebase Storage
   * @param storage - Firebase Storage instance
   * @param path - Storage path to the file
   * @param filename - Name to use for the downloaded file
   */
  static async downloadMediaFile(
    storage: FirebaseStorage,
    path: string,
    filename: string
  ): Promise<void> {
    const url = await getDownloadURL(ref(storage, path));
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(
        `Failed to download file: ${response.status} ${response.statusText}`
      );
    }
    const blob = await response.blob();
    const link = document.createElement('a');
    const objectUrl = URL.createObjectURL(blob);
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
      URL.revokeObjectURL(objectUrl);
      link.remove();
    });
  }

  /**
   * Resolves media references to email attachment objects
   * @param attachmentRefs - Array of media document references
   * @param emailRef - Reference to the email document that contains these attachments
   * @returns Array of email attachments or undefined if empty
   */
  static async resolveEmailAttachments(
    attachmentRefs: DocumentReference<IMedia>[],
    emailRef?: DocumentReference
  ): Promise<IEmailAttachment[] | undefined> {
    if (!attachmentRefs.length) {
      return undefined;
    }

    const resolved = await asyncForEach(attachmentRefs, async (mediaRef) => {
      const media = await Firestore.safeGetDoc<IMedia>(mediaRef);
      if (!media) {
        // eslint-disable-next-line no-console
        console.warn(`Could not resolve media attachment: ${mediaRef.path}`);
        return;
      }
      const metadata = media.metadata as
        | { type?: string; size?: number }
        | undefined;
      return {
        name: media.name,
        type: metadata?.type || 'application/octet-stream',
        size: metadata?.size ?? 0,
        mediaRef,
        emailRef,
      };
    });

    const attachments = compact(resolved);
    return attachments.length ? attachments : undefined;
  }
}
